<template>
  <Transition
    name="modal"
  >
    <div
      id="multifactor-authentication-modal-backdrop"
      class="modal-backdrop"
      @click="shakeModal"
    >
      <div
        id="multifactor-authentication-modal"
        class="multifactor-authentication-modal"
        :class="{ shake: modalShouldShake }"
      >
        <div class="multifactor-authentication-modal-container">
          <button
            class="navigate-backward"
            aria-label="Close modal"
            @click="navigateBackward"
          >
            <img
              alt=""
              loading="lazy"
              src="@/assets/images/chevron-right.svg"
            >
            Back
          </button>
          <header class="multifactor-authentication-modal-header">
            <img
              alt=""
              loading="lazy"
              src="@/assets/images/onetime-passcode-iconb.svg"
            >
            <span>Check your mobile phone</span>
          </header>
          <!-- OTP -->
          <div
            v-if="isOTP"
            id="modalDescription"
            class="multifactor-authentication-modal-content"
          >
            <strong> A verification code has been sent to {{ formattedPhoneNumber }}.</strong>
            This may take a few minutes. Once received, enter the verification code below.
          </div>
          <!-- SMFA -->
          <div
            v-else
            id="modalDescription"
            class="multifactor-authentication-modal-content"
          >
            Click on the verification link sent to {{ formattedPhoneNumber }} to confirm
            your identity. Then wait on this screen for {{ linkPollInterval }} seconds
            while we confirm your identity. <strong>You must click on the link before you can
              continue.</strong>
          </div>
          <!-- OTP -->
          <form
            v-if="isOTP"
            id="multifactor-authentication-form"
            class="multifactor-authentication-modal-form"
            @submit.prevent="validateForm"
          >
            <div class="multifactor-authentication-modal-otp-input-group">
              <label>Verification Code</label>
              <InputText
                v-model="oneTimePasscode"
                class="multifactor-authentication-modal-otp-input"
                type="text"
                name="oneTimePasscode"
                autocomplete="one-time-code"
                placeholder="000000"
                enterkeyhint="done"
                inputmode="numeric"
                length="6"
                :has-error="passcodeError.length > 0"
                @input="filterNonNumeric(), passcodeError = ''"
              >
                <span
                  id="OTP-error"
                  slot="helper"
                >
                  {{ passcodeError }}
                </span>
              </InputText>
            </div>
            <button
              v-if="isOTP"
              type="submit"
              class="multifactor-authentication-modal-verify-button"
              :disabled="buttonDisabled"
            >
              Verify
            </button>
          </form>
          <!-- SMFA -->
          <div
            v-show="!isOTP && !linkExpired"
            id="modalDescription"
            class="multifactor-authentication-modal-link-content"
          >
            Your new link will expire in <strong>{{ linkCountdown }}</strong> minutes.
            If your link has expired, or you did not receive a link, you will be able to
            request a new link.
          </div>
          <div
            v-show="!isOTP && linkExpired"
            id="modalDescription"
            class="multifactor-authentication-modal-link-content link-expired"
          >
            <img
              alt=""
              loading="lazy"
              src="@/assets/images/error-warning.svg"
            >
            <strong>Your link has expired. Click “Resend Link” to request a new link.</strong>
          </div>
          <!-- errors -->
          <div class="multifactor-authentication-resend-container">
            <!-- OTP -->
            <p
              v-show="isOTP && resendMessage"
              class="multifactor-authentication-resent-message"
            >
              <img
                alt=""
                loading="lazy"
                src="@/assets/images/green-checkmark.svg"
              >
              A new verification code has been sent to your mobile device.
            </p>
            <p
              v-if="passcodeFirstExpiration || passcodeHalfHourElpased"
              class="multifactor-authentication-expired-message"
            >
              <img
                alt=""
                loading="lazy"
                src="@/assets/images/red-warning-triangle.svg"
              >
              <strong>{{ passcodeExpirationText }}</strong>
            </p>
            <button
              v-show="isOTP && showResend"
              class="resend-multifactor-authentication"
              @click="resendVerification"
            >
              Resend Code
            </button>
            <!-- SMFA -->
            <p
              v-if="!isOTP && resendMessage"
              class="multifactor-authentication-resent-message"
            >
              <img
                alt=""
                loading="lazy"
                src="@/assets/images/green-checkmark.svg"
              >
              A new link has been sent to your mobile device.
            </p>
            <button
              v-show="!isOTP && showResend"
              class="resend-multifactor-authentication"
              @click="resendVerification"
            >
              Resend Link
            </button>
            <!-- OTP -->
            <div
              v-show="isOTP && showNote"
              class="multifactor-authentication-note"
            >
              <strong>Note:</strong> If you received multiple codes, only the most
              recent code will work. It may take some time for the latest code to arrive.
            </div>
            <!-- SMFA -->
            <div
              v-show="!isOTP && showNote"
              class="multifactor-authentication-note"
            >
              <strong>Note:</strong> If you received multiple links, only the most
              recent link will work. It may take some time for the latest link to arrive.
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { InputText } from '@mgo/ui/lib/components';
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'TwoFactor',
  components: {
    InputText,
  },
  props: {
    phoneNumber: {
      type: String,
      default: '',
      required: true,
    },
  },
  data: () => ({
    resendMessage: false,
    oneTimePasscode: '',
    passcodeError: '',
    showResend: false,
    passcodeHalfHourElpased: false,
    passcodeFirstExpiration: false,
    passcodeHalfHourTimeout: 1_800,
    passcodeTimeout: 300,
    passcodeTimerInterval: null,
    passcodeHalfHourTimerInterval: null,
    showNote: false,
    linkCountdown: '',
    linkExpired: false,
    linkTimerInterval: null,
    modalShouldShake: false,
    buttonDisabled: false,
  }),
  computed: {
    ...mapState(['mfa']),
    ...mapGetters({
      showLinkModal: 'mfa/showLinkModal',
      allowPayment: 'mfa/allowPayment',
      verificationFailure: 'mfa/verificationFailure',
      humanizedErrorMessage: 'mfa/humanizedErrorMessage',
    }),
    forceModalClose() {
      return this.mfa.verificationFailure;
    },
    passcodeExpirationText() {
      if (this.passcodeHalfHourElpased) return 'Your transaction has timed out. Click “Resend Code” to request a new code and continue.';
      return 'Your code expired. Click “Resend Code” to request a new code';
    },
    formattedPhoneNumber() {
      return this.phoneNumber ? this.phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : '';
    },
    linkPollInterval() {
      const interval = ((this.$store.get('config@twoFactorAuthPollInterval') % 60000) / 1000).toFixed(0);
      return `${interval}`;
    },
    isOTP() {
      return this.mfa.isOtp;
    },
  },
  async mounted() {
    if (this.mfa.isFetching) {
      this.$store.commit('ENABLE_LOADER');
    }

    // timers for both modals
    this.noteTimer();
    this.resendTimer();
    if (this.mfa.isOtp) {
      this.passcodeTimer();
    }

    if (!this.mfa.isOtp) {
      window.console.log('mfa linkVerify popup activated');
      this.linkCountdownTimer();
      // polling should be last statement
      await this.pollValidatingLink();
    }
  },
  updated() {
    this.$nextTick(() => {
      if (this.mfa.verificationFailure) {
        this.$router.push({ name: 'identity-check' });
      }
    });
  },
  methods: {
    async startModalShake() {
      this.modalShouldShake = true;
    },
    async shakeModal({ target: { id } }) {
      if (id === 'multifactor-authentication-modal-backdrop') {
        this.startModalShake();
        setTimeout(() => {
          this.modalShouldShake = false;
        }, 1000);
      }
    },
    async navigateBackward() {
      this.$store.dispatch('mfa/stopValidatingLink');
      this.$router.push({ name: 'verify-info-read' });
    },
    async resendVerification() {
      this.showResend = false;
      this.resendMessage = true;
      this.linkExpired = false;
      this.passcodeFirstExpiration = false;
      this.passcodeHalfHourElpased = false;
      this.passcodeHalfHourTimeout = 1_800;
      this.passcodeTimeout = 300;
      this.resendTimer();

      await this.$store.dispatch('mfa/resendVerification');
      if (this.$store.get('mfa/error').exception) {
        this.closeModal();
      }

      if (this.isOTP) {
        clearInterval(this.passcodeTimerInterval);
        clearInterval(this.passcodeHalfHourTimerInterval);
        this.passcodeTimer();
      }

      if (!this.isOTP) {
        clearInterval(this.linkTimerInterval);
        this.linkCountdownTimer();
      }
    },
    async validateForm() {
      if (this.oneTimePasscode.length === 6) {
        this.verifyPasscode();
      } else if (this.oneTimePasscode.length === 0) {
        this.passcodeError = 'This field cannot be empty.';
      } else {
        this.passcodeError = 'Verification Code must be 6 digits.';
      }
    },
    async verifyPasscode() {
      this.buttonDisabled = true;
      await this.$store.dispatch('mfa/validatePasscode', this.oneTimePasscode);
      const error = this.$store.get('mfa/error');
      if (error.exception) {
        switch (error.exception) {
          case 'IncorrectPasscodeException':
          case 'IvsValidationException':
            this.passcodeError = 'Invalid verification code. Try again or select “Resend Code” to receive a new code.';
            this.buttonDisabled = false;
            this.showResend = true;
            this.showNote = true;
            break;
          case 'PasscodeExpiredException':
            // displays passcode timeout message
            this.passcodeHalfHourElpased = false;
            this.passcodeFirstExpiration = true;
            this.buttonDisabled = false;
            break;
          case 'TransactionExpiredException':
            // displays transaction timeout message
            this.passcodeHalfHourElpased = true;
            this.passcodeFirstExpiration = false;
            this.buttonDisabled = false;
            break;
          case 'SystemErrorException':
          case 'PasscodeLockedException':
            // enrolled in IPP
            this.closeModal();
            break;
          default:
            window.console.log(`verifyCode returned ${error.exception} error - ${error.message}`);
            this.buttonDisabled = false;
        }
        return;
      }
      this.closeModal();
    },
    closeModal() {
      this.$emit('close');
    },
    async pollValidatingLink() {
      try {
        const { ivsVerificationPassed } = await this.$store.dispatch('mfa/pollValidatingLink');

        if (ivsVerificationPassed) {
          window.console.log('mfa linkVerify sucessful');
          // now close validateLink popup to allow navigation to payment
          this.closeModal();
        }
      } catch (pollException) {
        const { apiException, apiMessage } = pollException;
        switch (apiException) {
          case 'SystemErrorException':
            // All 500 http status code are reported as a SystemError.
            // Please display an error messege telling the user,
            // try some other time because there is a system is down.
            window.console.log(`verifyLink returned system error  - ${apiMessage}`);
            this.closeModal();
            break;
          case 'SmfaTimeoutException':
            // display timeout message on screen
            window.console.log(`verifyLink returned ${apiException} error  - ${apiMessage}`);
            this.closeModal();
            break;
          case 'SmfaRejectedException':
            // display no message
            window.console.log(`verifyLink returned ${apiException} error  - ${apiMessage}`);
            this.closeModal();
            break;
          default:
            window.console.error(`verifyLink failed ${apiException} - ${apiMessage}`);
            this.closeModal();
        }
      }
    },
    async resendTimer() {
      const resendWaitTime = this.$store.get('config@twoFactorAuthResendDelay');
      const timer = setTimeout(() => {
        this.showResend = true;
        this.resendMessage = false;
        clearTimeout(timer);
      }, resendWaitTime);
    },
    async passcodeHalfHourTimer() {
      const timer = setInterval(() => {
        this.passcodeHalfHourTimeout -= 1;
        if (this.passcodeHalfHourTimeout < 1) {
          this.passcodeFirstExpiration = false;
          this.passcodeHalfHourElpased = true;
          clearInterval(timer);
        }
      }, 1_000);
      this.passcodeHalfHourTimerInterval = timer;
    },
    async passcodeTimer() {
      this.passcodeHalfHourTimer();
      const timer = setInterval(() => {
        this.passcodeTimeout -= 1;
        if (this.passcodeTimeout < 1) {
          this.passcodeHalfHourElpased = false;
          this.passcodeFirstExpiration = true;
          clearInterval(timer);
        }
      }, 1_000);
      this.passcodeTimerInterval = timer;
    },
    async noteTimer() {
      const noteWaitTime = this.$store.get('config@twoFactorAuthResendDelay');
      const timer = setTimeout(() => {
        this.showNote = true;
        clearTimeout(timer);
      }, noteWaitTime);
    },
    millisToMinutesAndSeconds(time) {
      const minutes = Math.floor(time / 60000);
      const seconds = ((time % 60000) / 1000).toFixed(0);
      return `${minutes}:${(seconds < 10 ? '0' : '')}${seconds}`;
    },
    async linkCountdownTimer() {
      this.linkCountdown = this.millisToMinutesAndSeconds(this.$store.get('config@twoFactorAuthPollTimeout'));
      const end = new Date().getTime() + this.$store.get('config@twoFactorAuthPollTimeout');
      const timer = setInterval(() => {
        const now = new Date().getTime();
        const distance = end - now;
        this.linkCountdown = this.millisToMinutesAndSeconds(distance);
        if (distance < 0) {
          this.linkCountdown = '0:00';
          this.linkExpired = true;
          clearInterval(timer);
        }
      }, 1000);
      this.linkTimerInterval = timer;
    },
    filterNonNumeric() {
      // Replace non-numeric characters with an empty string
      this.oneTimePasscode = this.oneTimePasscode.replace(/[^0-9]/g, '');
    },
  },
};
</script>
